import { createTheme } from '@material-ui/core/styles';
import typography from '../base/typography.scss';

const LabsTheme = createTheme({
	//TYPOGRAPHY
	typography: {
		fontFamily: [
			"'Inter'",
			"'Helvetica Neue'",
			"'Segoe UI'",
			"'Roboto'",
			"'Arial'",
			"'BlinkMacSystemFont'",
			'sans-serif',
		].join(','),
		htmlFontSize: 16,
		h1: {
			lineHeight: typography.h1LineHeight,
			fontWeight: typography.h1FontWeight,
			fontSize: typography.h1FontSize,
			letterSpacing: typography.h1LetterSpacing,
		},
		h2: {
			lineHeight: typography.h2LineHeight,
			fontWeight: typography.h2FontWeight,
			fontSize: typography.h2FontSize,
			letterSpacing: typography.h2LetterSpacing,
		},
		h3: {
			lineHeight: typography.h3LineHeight,
			fontWeight: typography.h3FontWeight,
			fontSize: typography.h3FontSize,
			letterSpacing: typography.h3LetterSpacing,
		},
		h4: {
			lineHeight: typography.h4LineHeight,
			fontWeight: typography.h4FontWeight,
			fontSize: typography.h4FontSize,
			letterSpacing: typography.h4LetterSpacing,
		},
		h5: {
			lineHeight: typography.h5LineHeight,
			fontWeight: typography.h5FontWeight,
			fontSize: typography.h5FontSize,
			letterSpacing: typography.h5LetterSpacing,
		},
		h6: {
			lineHeight: typography.h6LineHeight,
			fontWeight: typography.h6FontWeight,
			fontSize: typography.h6FontSize,
		},
		subtitle1: {
			lineHeight: typography.sub1LineHeight,
			fontWeight: typography.sub1FontWeight,
			fontSize: typography.sub1FontSize,
		},
		subtitle2: {
			lineHeight: typography.sub2LineHeight,
			fontWeight: typography.sub2FontWeight,
			fontSize: typography.sub2FontSize,
			fontStyle: typography.sub2FontStyle,
			letterSpacing: typography.sub2LetterSpacing,
		},
		body1: {
			lineHeight: typography.body1LineHeight,
			fontWeight: typography.body1FontWeight,
			fontSize: typography.body1FontSize,
			letterSpacing: typography.body1LetterSpacing,
		},
		body2: {
			lineHeight: typography.body2LineHeight,
			fontWeight: typography.body2FontWeight,
			fontSize: typography.body2FontSize,
			letterSpacing: typography.body2LetterSpacing,
		},
		button: {
			lineHeight: typography.buttonLineHeight,
			fontWeight: typography.buttonFontWeight,
			fontSize: typography.buttonFontSize,
			letterSpacing: typography.buttonLetterSpacing,
		},
		caption: {
			lineHeight: typography.captionLineHeight,
			fontWeight: typography.captionFontWeight,
			fontSize: typography.captionFontSize,
			letterSpacing: typography.captionLetterSpacing,
		},
		overline: {
			lineHeight: typography.overlineLineHeight,
			fontWeight: typography.overlineFontWeight,
			fontSize: typography.overlineFontSize,
			letterSpacing: typography.overlineLetterSpacing,
		},
	},
	//COLORS
	palette: {
		primary: {
			// light: will be calculated from palette.primary.main,
			main: '#0042c2',
			// dark: will be calculated from palette.primary.main,
		},
		secondary: {
			// light: will be calculated from palette.secondary.main,
			main: '#495761',
			// dark: will be calculated from palette.secondary.main,
		},
		brand: { main: '#184896' },
		neutral: {
			n900: '#030405',
			n800: '#13171c',
			n700: '#242a33',
			n600: '#343d4a',
			n500: '#455060',
			n450: '#586271',
			n400: '#6c7582',
			n350: '#7f8793',
			n300: '#939aa4',
			n250: '#a7acb5',
			n200: '#babfc5',
			n150: '#ced1d6',
			n100: '#e2e4e7',
			n50: '#f6f6f7',
		},
		alerts: {
			error: '#e74c3c',
			warning: '#e67e22',
			success: '#2ecc71',
			info: '#0081c6',
		},
	},
	//COMPONENT OVERRIDES
	overrides: {
		//AVATAR
		MuiAvatar: {
			root: {
				width: '5.5rem',
				height: '5.5rem',
			},
			colorDefault: { backgroundColor: '#242a33' },
			img: { objectFit: 'contain' },
		},
		//BADGE
		MuiBadge: {
			badge: {
				backgroundColor: '#6c7582',
				color: '#ffffff',
			},
		},
		//BUTTON
		MuiButtonBase: {
			root: {
				justifyContent: 'flex-start',
				padding: '1rem 1.5rem',
				textTransform: 'none',
			},
		},
		MuiButton: {
			root: {
				color: '#6c7582',
				textTransform: 'none',
			},
			outlined: {
				padding: '1rem 1.5rem',
				borderRadius: 0,
			},
			contained: {
				padding: '1rem 1.5rem',
				boxShadow: 'unset',
				borderRadius: 0,
			},
			containedPrimary: { color: 'white' },
			outlinedSecondary: {
				padding: '0.8rem 1.5rem',
				borderWidth: '0.125rem',
				boxSizing: 'content-box',
				borderRadius: 0,
				'&:hover': {
					padding: '0.8rem 1.5rem',
					borderWidth: '0.125rem',
					boxSizing: 'content-box',
					borderRadius: 0,
				},
			},
		},
		//CHIP
		MuiChip: {
			root: { backgroundColor: '#e2e4e7' },
		},
		//ICON BUTTON
		MuiIconButton: {
			root: { color: '#6c7582' },
		},
		//INPUT CONTROLS
		MuiInputBase: {
			root: {
				height: '3.25rem',
				background: '#ffffff',
			},
		},
		MuiOutlinedInput: { root: { borderRadius: 0 } },
		//SELECT
		MuiSelect: {
			root: { minHeight: '3rem' },
			selectMenu: {
				minHeight: '3rem',
				display: 'flex',
				alignItems: 'center',
				padding: '0 1rem',
			},
		},
		//STEPPER
		MuiStepper: {
			root: {
				backgroundColor: 'unset',
				padding: 'unset',
			},
		},
		//TABS
		MuiTab: {
			root: {
				letterSpacing: '-0.025rem',
				'@media (min-width: 600px)': { minWidth: '14rem' },
				'&$textColorInherit': { opacity: 1 },
				'&$selected': {
					backgroundColor: '#e2e4e7',
					fontWeight: 600,
				},
			},
			wrapper: {
				display: 'flex',
				flexDirection: 'row',
				alignItems: 'center',
				marginLeft: '-0.5rem',
			},
			labelIcon: {
				color: '#6c7582',
				maxHeight: '3.25rem',
				minHeight: '3.25rem',
				paddingTop: '0.25rem',
			},
		},
		//TABLE
		MuiTablePagination: {
			root: { display: 'flex' },
		},
		//TABLECELL
		MuiTableCell: {
			root: { alignItems: 'center' },
		},
		//TOOLTIP
		MuiTooltip: {
			tooltip: { fontSize: '0.75rem' },
		},
	},
});

export default LabsTheme;
